'use client';

import { useMemo, useState } from 'react';

import { useTranslation } from 'react-i18next';

import { useNativeLayout } from '~/(tenant)/_components/native/_lib/native-layout-context';

import {
  type ClassEligibility,
  EMPTY_CLASS_FILTERS,
  countActiveClassFilters,
  getDateWeekday,
  matchesClassFilters,
} from '../_lib/class-filters';
import type { AvailableClassData } from '../_lib/server/server-actions';
import { ClassCard } from './class-card';
import { ClassFilters, NoMatchingClasses } from './class-filters';

interface ClassSelectionProps {
  classes: AvailableClassData[];
  selectedClass: string;
  onClassSelect: (
    bookingId: string,
    price: number,
    templateId: string | null,
    isFull?: boolean,
  ) => void;
  selectedDate: Date;
  isLoading?: boolean;
  /** Class-type eligibility rules that drive the required-field filters. */
  eligibilityByClassType?: Record<string, ClassEligibility[]>;
}

export function ClassSelection({
  classes,
  selectedClass,
  onClassSelect,
  selectedDate,
  isLoading = false,
  eligibilityByClassType = {},
}: ClassSelectionProps) {
  const { t } = useTranslation('bookings');
  const { filters, setFilters, filterable, visibleClasses } =
    useFilteredClasses(classes, eligibilityByClassType);
  const filterRow =
    classes.length > 0 || countActiveClassFilters(filters) > 0 ? (
      <div className="mb-2">
        <ClassFilters
          classes={filterable}
          value={filters}
          onChange={setFilters}
        />
      </div>
    ) : null;

  if (isLoading) {
    return (
      <>
        {countActiveClassFilters(filters) > 0 ? filterRow : null}
        <ClassListSkeleton />
      </>
    );
  }

  if (classes.length === 0 && countActiveClassFilters(filters) === 0) {
    return <NoClassesAvailable message={t('noClassesAvailable')} />;
  }

  if (visibleClasses.length === 0) {
    return (
      <>
        {filterRow}
        <NoMatchingClasses onClear={() => setFilters(EMPTY_CLASS_FILTERS)} />
      </>
    );
  }

  return (
    <>
      {filterRow}
      <div className="-m-1 space-y-2 p-1">
        {visibleClasses.map((classData) => (
          <ClassCard
            key={classData.booking_id}
            classData={classData}
            isSelected={selectedClass === classData.booking_id}
            onSelect={() =>
              onClassSelect(
                classData.booking_id,
                classData.price_per_person,
                classData.template_id,
                classData.is_full || classData.virtually_full,
              )
            }
            selectedDate={selectedDate}
          />
        ))}
      </div>
    </>
  );
}

export function useFilteredClasses(
  classes: AvailableClassData[],
  eligibilityByClassType: Record<string, ClassEligibility[]>,
) {
  const [filters, setFilters] = useState(EMPTY_CLASS_FILTERS);
  // Memoized so a list spanning many days isn't rebuilt on unrelated renders.
  const filterable = useMemo(
    () =>
      classes.map((classData) => ({
        classData,
        slots: [
          {
            weekday: getDateWeekday(classData.booking_date),
            startTime: classData.start_time,
          },
        ],
        eligibility: classData.class_type_id
          ? (eligibilityByClassType[classData.class_type_id] ?? [])
          : [],
      })),
    [classes, eligibilityByClassType],
  );
  const visibleClasses = useMemo(
    () =>
      filterable
        .filter((entry) => matchesClassFilters(entry, filters))
        .map((entry) => entry.classData),
    [filterable, filters],
  );

  return { filters, setFilters, filterable, visibleClasses };
}

export function ClassListSkeleton() {
  const isNativeLayout = useNativeLayout();

  return (
    <div className="space-y-2">
      {[1, 2, 3].map((i) => (
        <div
          key={i}
          style={
            isNativeLayout
              ? { borderRadius: 'var(--native-booking-radius)' }
              : undefined
          }
          className="bg-muted/30 min-h-[116px] animate-pulse rounded-xl border"
        />
      ))}
    </div>
  );
}

export function NoClassesAvailable({ message }: { message: string }) {
  return (
    <div className="bg-muted/30 rounded-lg border p-6 text-center">
      <p className="text-muted-foreground">{message}</p>
    </div>
  );
}
